import { useState } from 'react';
import { toast } from 'sonner';
import { openAiService } from '@/services/ai';

export const useTitleGenerator = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeature, setActiveFeature] = useState<string | undefined>(undefined);

  const generateTitles = async (content: string, currentTitle: string) => {
    try {
      if (!content || content.trim().length === 0) {
        toast.error("Cannot generate titles for empty content. Please add some text to your article first.");
        return { 
          success: false, 
          error: "Cannot generate titles for empty content" 
        };
      }

      setIsProcessing(true);
      setActiveFeature('generateTitle');
      
      console.log("Generating titles for content length:", content.length);
      console.log("Current title:", currentTitle);

      const systemPrompt = `You are a professional headline writer for a journalistic platform. 
      Generate 5 compelling, varied title options based on the article content.
      Include a mix of styles: informative, engaging, SEO-friendly, question-based, and concise.
      Each title should accurately represent the content while being attention-grabbing.
      Maximum length: 70 characters per title.
      Format as a JSON array of objects with 'title' and 'style' properties.`;

      const prompt = `Generate 5 varied headline options for this article${currentTitle ? ` (current title: "${currentTitle}")` : ''}. 
      The content is about: ${content.substring(0, 1500)}${content.length > 1500 ? '...' : ''}`;

      const response = await openAiService.generateContent({
        systemPrompt,
        prompt,
        temperature: 0.8,
        maxTokens: 500,
        model: 'gpt-4o-mini',
        content
      });

      setIsProcessing(false);
      setActiveFeature(undefined);

      if (!response.success) {
        console.error("Error generating titles:", response.error);
        toast.error(`Failed to generate titles: ${response.error || "Unknown error"}`);
        return response;
      }

      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        console.error("Empty response from AI service");
        toast.error("Received empty response from AI service. Please try again.");
        return { 
          success: false, 
          error: "Empty response from AI service" 
        };
      }

      console.log("Title generation successful, response:", response.data);
      return response;
    } catch (error) {
      setIsProcessing(false);
      setActiveFeature(undefined);
      console.error("Error generating titles:", error);
      toast.error("Failed to generate titles. Please try again.");
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error" 
      };
    }
  };

  return {
    generateTitles,
    isProcessing,
    activeFeature
  };
};
